import { CSSProperties } from "react";
import type { BadgeTierMeta } from "@/lib/store";

/**
 * BadgeTierChip
 *
 * Compact pill displaying a sister's earned tier (Beginning, Planted, Rooted,
 * Bearing Fruit). Tone shifts by tier so a glance reveals her season at a
 * glance: forest for the seedling beginning, fiber-green for Planted, deep
 * forest for Rooted, gold for Bearing Fruit.
 *
 * The "none" tier is rendered as a soft "Beginning" pill, never absent. We
 * never want a sister to feel she has "no" badge.
 */
export default function BadgeTierChip({
  tier,
  size = "md",
  withEmoji = true,
}: {
  tier: BadgeTierMeta;
  size?: "sm" | "md";
  withEmoji?: boolean;
}) {
  const fontSize = size === "sm" ? 9.5 : 11;
  const padding = size === "sm" ? "3px 9px" : "4px 11px";

  const palette = paletteFor(tier.tone);

  const style: CSSProperties = {
    padding,
    borderRadius: 999,
    fontSize,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: palette.bg,
    color: palette.text,
    border: `1px solid ${palette.border}`,
  };

  return (
    <span
      className="mnf-body"
      style={style}
      data-testid={`chip-tier-${tier.key}`}
    >
      {withEmoji && <span aria-hidden>{tier.emoji}</span>}
      {tier.label}
    </span>
  );
}

function paletteFor(tone: BadgeTierMeta["tone"]) {
  switch (tone) {
    case "gold":
      return {
        bg: "var(--mnf-gold2)",
        text: "var(--mnf-gold)",
        border: "rgba(160,120,32,0.22)",
      };
    case "fiber":
      return {
        bg: "var(--mnf-fiberBg)",
        text: "#2C6248",
        border: "var(--mnf-fiberBorder)",
      };
    case "coral":
      return {
        bg: "rgba(192,120,74,0.10)",
        text: "var(--mnf-t)",
        border: "rgba(192,120,74,0.22)",
      };
    case "forest":
    default:
      return {
        bg: "rgba(30,74,53,0.08)",
        text: "var(--mnf-f)",
        border: "rgba(30,74,53,0.14)",
      };
  }
}
